import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

import { validatePolicyFile } from '@/lib/validatePolicyFile';

interface UploadVersionParams {
  policyId: string;
  file: File;
  isSubstantive: boolean;
  nextReviewDate?: string;
  versionNotes?: string;
}

interface UploadVersionRpcResult {
  version_id: string;
  version_number: number;
}

export function useUploadPolicyVersion() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useMutation({
    mutationFn: async ({ policyId, file, isSubstantive, nextReviewDate, versionNotes }: UploadVersionParams) => {
      if (!profile) throw new Error('Not authenticated');

      // Validate file before any upload attempt
      const validation = validatePolicyFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Storage path: clinic_id/policy_id/<uuid>.ext
      // version_number is stored in the DB row; filename only needs to be unique
      // within the clinic/policy prefix. Using crypto.randomUUID() avoids any
      // dependency on version_number (which is now computed server-side).
      const fileExt = file.name.split('.').pop();
      const fileName = `${profile.clinic_id}/${policyId}/${crypto.randomUUID()}.${fileExt}`;

      // Upload to storage
      setUploadProgress(10);
      const { error: uploadError } = await supabase.storage
        .from('policy-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;
      setUploadProgress(70);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('policy-documents')
        .getPublicUrl(fileName);

      // Atomic version creation via SECURITY DEFINER RPC.
      // Server-side: computes version_number under policy row lock, sets
      // version_status='pending_approval', derives created_by from auth.uid(),
      // flips policies.status if currently draft, writes audit log — all in
      // one transaction. Closes the OWASP A04 hole where clients could forge
      // version_status by inserting directly.
      const { data: rpcData, error: rpcError } = await supabase.rpc('upload_policy_version_record', {
        p_policy_id: policyId,
        p_file_url: urlData.publicUrl,
        p_is_substantive: isSubstantive,
        p_next_review: isSubstantive && nextReviewDate ? nextReviewDate : null,
        p_notes: versionNotes || null,
      });

      if (rpcError) throw rpcError;

      const { version_id: newVersionId, version_number: nextVersion } =
        rpcData as unknown as UploadVersionRpcResult;

      // Fire-and-forget: notify admins that a version is pending approval.
      // Mirrors the same pattern in useComposerSubmit (Step 6).
      // No-op for revision submissions (Approved policy) since
      // send-approval-notifications only scans status='pending_approval' —
      // harmless to call regardless.
      {
        const { data: { session: notifySession } } = await supabase.auth.getSession();
        if (notifySession?.access_token) {
          fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/trigger-approval-notification`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${notifySession.access_token}`,
              },
            }
          ).catch((err) => {
            console.error('[trigger-approval-notification] Fire-and-forget failed:', err);
          });
        }
      }

      setUploadProgress(100);

      return { version: nextVersion, fileUrl: urlData.publicUrl, versionId: newVersionId };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['policy-versions', variables.policyId] });
      queryClient.invalidateQueries({ queryKey: ['policy', variables.policyId] });
      queryClient.invalidateQueries({ queryKey: ['pending-acknowledgements'] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      setUploadProgress(0);
    },
    onError: () => {
      setUploadProgress(0);
    },
  });

  return {
    uploadVersion: mutation.mutate,
    isUploading: mutation.isPending,
    uploadProgress,
    error: mutation.error,
    reset: mutation.reset,
  };
}
